import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {NavbarService} from '../primary-nav/primary-nav.service';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {SecurityQuestion} from '../my-profile/model/security-question';
import {MyProfileService} from '../my-profile/service/my-profile.service';
import {compareValidator} from '../shared/directives/compare-validator.directive';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {Alert, AlertType} from '../shared/alert.model';
import {getAlertMessage} from '../shared/api-loaders';
import {MessageService} from '../shared/message.service';
import {SqResetPassword} from './model/sq-reset-password';
import { HttpParams } from '@angular/common/http';
import { UserService } from '../users/services/user.service';
import { PasswordValidationService } from '../shared/services/password-validation.service';
import { ClaimsService } from '../shared/claims.service';

@Component({
    selector: 're-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit, OnDestroy {

  fullName: string;
  answerSQForm: UntypedFormGroup;
  // Password Requirements
  userName = new UntypedFormControl('');
  sourceURL: string;
  sourceDomain: string;
  alert: Alert;
  showMessage: boolean;
  sqResetPassword: SqResetPassword = new SqResetPassword();
  success: boolean;
  allowPasswordChange: boolean;
  failedLoginAttempts: number;
  questions: SecurityQuestion[];

  private reLowerCase = /(?=.*[a-z])/;
  private reUpperCase = /(?=.*[A-Z])/;
  private reNumberCase = /(?=.*\d)/;
  private reSpecialCharacterCase = /(?=(?:.*?[!@#$%*()_+^&}{:;?.]){1})/;
  private reMinLengthCase = /(?=^.{9,}$)/;
  private sub: Subscription;
  private messageSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private myProfileService: MyProfileService,
    private nav: NavbarService,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private router: Router,
    private cd: ChangeDetectorRef,
    public userService: UserService,
    private claimsService: ClaimsService,
    private passwordValidationSvc: PasswordValidationService
  ) {

    this.nav.forgot = true;
    this.createAnswerSQForm();
    this.messageSubscription = this.messageService.get().subscribe(message => {
      window.scroll(0, 0);
      if (message) {
        if (message instanceof Alert) {
          this.alert = message;
        }
      }
    })
  }

  ngOnInit() {
    this.messageService.showSpinner();

    this.sub = this.route.queryParams.subscribe((params: Params) => {
      let userName = params['userName'];
      this.sourceURL = params['sourceURL'];
      this.sourceDomain = params['sourceDomain'];
      this.updateUserName(userName);
    });

    if (this.userName) {
      this.getUserSecurityQuestions(this.userName.value);
    } else {
      this.messageService.send({ type: AlertType.DANGER, message: 'Information entered is not valid. Please check with your system administrator.'});
    }
  }

  getUserSecurityQuestions(userName: string) {
    let params = new HttpParams();

    userName = this.appendDomain(userName);
    params = params.set('userName', userName);

    this.myProfileService.myProfile_SecurityQuestions(params).subscribe(res => {
      // Determine the number of failed login attempts.
      this.failedLoginAttempts = res.failedAttempts;

      if (this.failedLoginAttempts > 4) {
        this.messageService.send(new Alert(AlertType.DANGER, 'Please contact your system administrator to reset password.'));
            this.messageService.hideSpinner();
      } else {
        // Save the security questions so they can be used if the question answers need to be cleared for a retry.
        this.questions = res.securityQuestions;
        this.setSecurityQuestions();
        this.success = true;

        if (this.failedLoginAttempts > 0) {
          this.messageService.send(new Alert(AlertType.WARNING, 'There are ' + (5 - this.failedLoginAttempts) + ' attempts remaining.'));
        } else {
          this.alert = null;
        }

            this.messageService.hideSpinner();
      }
    },
      err => {
        this.messageService.send(getAlertMessage(err));
            this.messageService.hideSpinner();
      })
  }

  appendDomain(userName: string) {
    if (this.sourceDomain && userName.indexOf('@') < 0) {
      if (this.sourceDomain.indexOf('@') >= 0) {
        return `${userName}${this.sourceDomain}`;
      } else {
        return `${userName}@${this.sourceDomain}`;
      }
    } else {
      return userName;
    }
  }

  updateUserName(userName: string) {
    this.userName.setValue(userName);
  }

  createAnswerSQForm() {
    this.answerSQForm = this.fb.group({
      securityQuestions: [this.fb.array([]), Validators.required],
      newPassword: [''],
      confirmPassword: [''],
      userName: [this.userName]
    });
  }

  get securityQuestions(): UntypedFormArray {
    return this.answerSQForm.get('securityQuestions') as UntypedFormArray;
  };

  get securityQuestionAnswers(): UntypedFormArray {
    return this.answerSQForm.get('answer') as UntypedFormArray;
  };

  setSecurityQuestions() {
    const securityQFGs = this.questions.map(securityQuestion => this.fb.group(securityQuestion));
    const securityQFormArray = this.fb.array(securityQFGs);
    this.answerSQForm.setControl('securityQuestions', securityQFormArray);
  }

  ngOnDestroy() {
    if (this.sub && !this.sub.closed) {
      this.sub.unsubscribe();
    }
  }

  validateSecurityQuestions() {
    this.messageService.showSpinner();
    const securityQuestions = Object.assign({}, this.sqResetPassword, this.answerSQForm.value);

    securityQuestions.userName = this.appendDomain(this.userName.value);
    securityQuestions.validate = true;

    this.myProfileService.myProfile_sqResetPassword(securityQuestions).subscribe(res => {
      // Determine the number of failed login attempts.
      this.failedLoginAttempts = res.failedAttempts;

      if (this.failedLoginAttempts > 4) {
        this.success = false;
        this.messageService.send(new Alert(AlertType.DANGER, 'Please contact your system administrator to reset password.'));
            this.messageService.hideSpinner();
      } else if (this.failedLoginAttempts > 0) {
        this.messageService.send(new Alert(AlertType.WARNING, 'Answers incorrect, there are ' + (5 - this.failedLoginAttempts) + ' attempts remaining.'));
        this.setSecurityQuestions();
            this.messageService.hideSpinner();
        this.success = true;
      } else {
        // Add validators for the password controls.
        this.answerSQForm.controls['newPassword'].setValidators([Validators.required]);
        this.answerSQForm.controls['confirmPassword'].setValidators([Validators.required, compareValidator('newPassword')]);

            this.messageService.hideSpinner();
        this.allowPasswordChange = true;
        this.alert = null;
      }
    },
      err => {
        this.messageService.send(getAlertMessage(err));
            this.messageService.hideSpinner();
      })
  }

  answerSQForm_onSubmit() {
    this.messageService.showSpinner();
    const securityQuestions = Object.assign({}, this.sqResetPassword, this.answerSQForm.value);

    securityQuestions.userName = this.appendDomain(this.userName.value);
    securityQuestions.validate = false;

    this.myProfileService.myProfile_sqResetPassword(securityQuestions).subscribe(res => {
          this.messageService.hideSpinner();
      this.redirectBack();
    },
      err => {
        this.messageService.send(getAlertMessage(err));
            this.messageService.hideSpinner();
      })
  }

  // TODO: Have to revisit and see if we can move this to a custom directive.
  valueChange() {
  }

  validatePassword() {
    return this.passwordValidationSvc.validatePassword(this.answerSQForm.value.newPassword, this.userName.value, null, null, null, false, true, true).isValid();
  }

  redirectBack() {
    this.nav.forgot = false;
    if (this.sourceURL) {
      window.location.href = this.sourceURL;
    } else {
      this.router.navigate(['/']);
    }
  }
}
